import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, Mail, ArrowLeft, Send } from "lucide-react";
import { Input } from "@/components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetLink, setResetLink] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      if (res.data.reset_token) {
        setResetLink(`/reset-password?token=${res.data.reset_token}`);
      }
      toast.success("Reset link generated!");
    } catch (e) {
      toast.error("Failed to process request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neo-bg dark:bg-[#141414] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-neo-yellow border-[3px] border-neo-border dark:border-[#444] shadow-[4px_4px_0px_0px_#1A1A1A] dark:shadow-[4px_4px_0px_0px_#444] flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-neo-text" />
          </div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight dark:text-white">Reset Password</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-2">We'll help you get back into your account</p>
        </div>

        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
          {!sent ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`neo-btn w-full py-3 ${loading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"}`}
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-neo-text border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Reset Link
                  </span>
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-4" data-testid="reset-link-sent">
              <div className="w-14 h-14 rounded-full bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-4">
                <Send className="w-7 h-7 text-neo-text" />
              </div>
              <h2 className="font-heading font-bold text-xl mb-2 dark:text-white">Reset Link Generated</h2>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400 mb-4">
                A password reset link has been created for your account.
              </p>
              {resetLink && (
                <Link
                  to={resetLink}
                  className="neo-btn neo-btn-primary inline-flex items-center gap-2 mb-4"
                  data-testid="reset-link-btn"
                >
                  Reset Password Now
                </Link>
              )}
            </div>
          )}

          <div className="mt-4 pt-4 border-t-2 border-neo-border/20 text-center">
            <button
              onClick={() => navigate("/login")}
              className="text-sm text-neo-text-secondary dark:text-gray-400 hover:text-neo-text dark:hover:text-white flex items-center justify-center gap-1 mx-auto"
              data-testid="back-to-login-btn"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
